import { Observable, catchError, from, switchMap, throwError } from 'rxjs';

import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';

import { environment } from '@environments/environment';

import { SessionTokenStore } from '@cxc/core/services';
import { SessionRefreshService } from '@services/session/session-refresh.service';
import { sessionRejected } from '@services/session/session-rejected';
import { SessionService } from '@services/session/session.service';

/**
 * Our APIs: every request to them carries the Bearer token by default
 * (LEN-2764). A relative `/api/` URL is the local dev proxy to identity-api.
 * Anything else (translations, signed GCS URLs, other hosts) gets no token
 * unless it opts in with `Auth-Opt-In: true`, as AIVA's client does.
 */
const API_ORIGINS = [
  environment.PORTAL_API,
  environment.FACILITIES_API,
  environment.COMPANIES_API,
].filter(Boolean);

/** identity-api routes that are called before there is a session. */
const PUBLIC_AUTH_PATHS = [
  '/api/v1/identity/auth/login',
  '/api/v1/identity/auth/verify-2fa',
  '/api/v1/identity/auth/signup',
  '/api/v1/identity/auth/forgot-password',
  '/api/v1/identity/auth/confirm-forgot-password',
  '/api/v1/identity/auth/refresh-token',
  '/api/v1/identity/invitations/',
];

/** A 401 from these must not trigger a refresh: it would loop. */
const NO_REFRESH_PATHS = [...PUBLIC_AUTH_PATHS, '/api/v1/identity/auth/logout'];

function isApiRequest(url: string): boolean {
  return (
    url.startsWith('/api/') ||
    API_ORIGINS.some(origin => url.startsWith(`${origin}/`))
  );
}

/** Matched on the path, so a query string carrying a public path does not count. */
function matchesAny(url: string, paths: string[]): boolean {
  const { pathname } = new URL(url, 'http://relative.invalid');
  return paths.some(path => pathname.startsWith(path));
}

/** A 401 from this URL is final: the authorization interceptor will not retry it. */
export function isNoRefreshPath(url: string): boolean {
  return matchesAny(url, NO_REFRESH_PATHS);
}

function withToken(
  req: HttpRequest<unknown>,
  token: string | null
): HttpRequest<unknown> {
  return token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;
}

export function httpAuthorizationInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const optIn = req.headers.get('Auth-Opt-In');
  if (optIn !== null) {
    req = req.clone({ headers: req.headers.delete('Auth-Opt-In') });
  }

  const carriesToken =
    optIn === 'true' ||
    (optIn === null &&
      isApiRequest(req.url) &&
      !matchesAny(req.url, PUBLIC_AUTH_PATHS));

  if (!carriesToken) {
    return next(req);
  }

  const tokens = inject(SessionTokenStore);
  const refresher = inject(SessionRefreshService);
  const session = inject(SessionService);

  return next(withToken(req, tokens.token())).pipe(
    catchError((error: unknown) => {
      // Only our APIs speak for our session: an opted-in foreign host (AIVA)
      // answering 401 must not refresh, and so must not end in a logout.
      const expired =
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        isApiRequest(req.url) &&
        !matchesAny(req.url, NO_REFRESH_PATHS);
      if (!expired) {
        return throwError(() => error);
      }

      // The access token expired: get a new one from the refresh cookie and
      // retry once. Concurrent 401s share the same refresh.
      return from(refresher.refresh()).pipe(
        catchError((refreshError: unknown) => {
          if (sessionRejected(refreshError)) {
            void session.logout();
          }
          return throwError(() => error);
        }),
        switchMap(token => next(withToken(req, token)))
      );
    })
  );
}
